import { Login } from "@/components/Dialogs/Login";
import Spinner from "@/components/Spinner";
import BotTag from "@/components/Tags/Bot";
import Icon from "@/icons";
import permissions from "@/permissions";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

export default function InviteBot({ query }: { query: any }) {
    const { botId } = useParams();

    const botQuery = useQuery({
        queryKey: ["bot", botId],
        retry: 1,
        retryDelay: 1000,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        queryFn: async () => {
            const response = await fetch(`__SITE_BASE__/api/v1/users/${botId}`);
            return response.json();
        }
    });

    const [searchParams] = useSearchParams();

    const permissionsDetails = [
        {
            name: "Administrator",
            onMap: "ADMINISTRATOR",
            description: "Allows you to update the team settings.",
            map: permissions.get("ADMINISTRATOR"),
        },
        {
            name: "Manage Channels",
            onMap: "MANAGE_CHANNELS",
            description: "Allows you to manage channels.",
            map: permissions.get("MANAGE_CHANNELS"),
        },
        {
            name: "Manage Roles",
            onMap: "MANAGE_ROLES",
            description: "Allows you to manage roles.",
            map: permissions.get("MANAGE_ROLES"),
        },
        {
            name: "Manage Team",
            onMap: "MANAGE_TEAM",
            description: "Allows you to manage the team.",
            map: permissions.get("MANAGE_TEAM"),
        },
        {
            name: "View Audit Log",
            onMap: "VIEW_AUDIT_LOG",
            description: "Allows you to view the audit log.",
            map: permissions.get("VIEW_AUDIT_LOG"),
        },
        {
            name: "Ban Members",
            onMap: "BAN_MEMBERS",
            description: "Allows you to ban members.",
            map: permissions.get("BAN_MEMBERS"),
        },
        {
            name: "Delete Messages",
            onMap: "DELETE_MESSAGES",
            description: "Allows you to delete messages.",
            map: permissions.get("DELETE_MESSAGES"),
        },
        {
            name: "Manage Applications",
            onMap: "MANAGE_APPLICATIONS",
            description: "Allows you to manage applications.",
            map: permissions.get("MANAGE_APPLICATIONS"),
        },
        {
            name: "Join Tournaments",
            onMap: "JOIN_TOURNAMENTS",
            description: "Allows you to join tournaments.",
            map: permissions.get("JOIN_TOURNAMENTS"),
        },
        {
            name: "Create Invites",
            onMap: "CREATE_INVITES",
            description: "Allows you to create invites.",
            map: permissions.get("CREATE_INVITES"),
        }
    ]

    const [selectedPermission, setSelectedPermission] = useState(
        searchParams.get("permissions") == "*" ? permissionsDetails.map((permission) => Number(permission.map)).reduce((a, b) => a + (b ?? 0), 0).toString() :
            searchParams.get("permissions") || "0"
    );
    const [selectedServerId, setSelectedServerId] = useState("");

    const getHavePermissionTeams = useQuery({
        queryKey: ["teams"],
        retry: 1,
        retryDelay: 1000,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        queryFn: async () => {
            const response = await fetch(`__SITE_BASE__/api/v1/teams?onlyManageTeamPermission=true`);
            return response.json();
        }
    });
    const [error, setError] = useState("");

    useEffect(() => {
        if (botQuery.isFetched && botQuery.data && !botQuery?.data?.user?.bot) {
            setError("This bot does not exist");
        }
    }, [botQuery, setError]);
    const navigate = useNavigate();
    const addBot = async () => {
        if (selectedServerId === "") return;

        const response = await fetch(`__SITE_BASE__/api/v1/teams/${selectedServerId}/bots`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                botId,
                botPermissions: selectedPermission,
            }),
        });

        if (response.ok) {
            navigate("/teams/" + selectedServerId);
        } else {
            const data = await response.json();
            setError(data.message);
        }
    }

    return (
        <div className="h-screen w-full flex items-center justify-center bg-gradient-to-br from-brand-darkest to-brand-dark shadow-2xl">
            <div className="bg-brand-back text-white rounded-lg shadow-lg max-w-lg w-full">
                {botQuery.isLoading && (
                    <div className="flex items-center justify-center h-32">
                        <Spinner />
                    </div>
                )}

                {botQuery.data?.user && botQuery?.data?.user?.bot && !error && (
                    <>
                        <div className="p-8">
                            <div className="flex items-center gap-4 pb-6 border-b border-brand-green relative px-3 py-4">
                                <div className="absolute top-0 left-0 h-full z-10 w-full rounded-lg bg-gradient-to-br from-brand-darkest to-brand-dark">

                                </div>
                                {botQuery.data?.user?.profilePicture && (
                                    <img
                                        src={botQuery.data?.user?.profilePicture}
                                        className="rounded-full h-16 w-16 border-2 border-brand-default z-20"
                                        alt="Bot profile"
                                    />
                                ) || (
                                        <div className="rounded-full h-16 w-16 bg-brand-default z-20 flex items-center justify-center text-2xl font-semibold">
                                            {botQuery.data?.user?.username[0]}
                                        </div>
                                    )}

                                <div>
                                    <div className="flex items-center relative   z-20">
                                        <h1 className="text-2xl font-bold">{botQuery.data?.user?.username}</h1>
                                        <BotTag user={botQuery.data?.user} />
                                    </div>
                                    <p className="text-sm text-gray-400 relative z-20">Bot Invite</p>
                                </div>
                            </div>

                            <div className="flex flex-col items-start">
                                <div className="flex items-center gap-4 mb-4">
                                </div>


                                <div className="w-full">
                                    <p className="text-lg font-semibold text-gray-200 mb-2">Select a server to invite the bot</p>
                                    <select
                                        className="w-full bg-brand-darkest text-gray-100 font-semibold py-3 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-default"
                                        onChange={(e) => setSelectedServerId(e.target.value)}
                                    >
                                        <option value="">Select a server</option>
                                        {getHavePermissionTeams.data?.teams.map((team: any) => (
                                            <option key={team.id} value={team.id}>
                                                {team.name}
                                            </option>
                                        ))}
                                    </select>
                                    <h4 className="text-gray-400 text-sm mt-2">You can only invite the bot to a server where you have the "Manage Team" permission.</h4>
                                </div>

                                <div className="w-full mt-4">
                                    <p className="text-lg font-semibold text-gray-200 mb-2 mt-4">Select a permissions</p>
                                    <div className="overflow-y-auto  max-h-36 px-4" style={{ scrollbarWidth: "thin", scrollbarColor: "rgb(40, 160, 42) rgb(39 42 47)" }}>
                                        {permissionsDetails.map((permission) => (
                                            <div
                                                key={permission.name}
                                                className="flex items-center justify-between w-full bg-brand-darker rounded-lg mt-2"
                                            >
                                                <div className="flex items-center gap-2">
                                                    <div className="flex flex-col items-start w-64">
                                                        <span className="text-white font-semibold">{permission.name}</span>
                                                    </div>
                                                </div>
                                                <div
                                                    className="relative inline-flex items-center cursor-pointer"
                                                    onClick={() => {
                                                        setSelectedPermission(
                                                            (Number(selectedPermission) ^ (1 << permissionsDetails.indexOf(permission))).toString()
                                                        );
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className="sr-only"
                                                        checked={permissions.get(permission.onMap) !== undefined && (Number(selectedPermission)
                                                            & Number(permissions.get(permission.onMap) ?? 0)) !== 0}
                                                    />
                                                    <div
                                                        className={`block w-14 h-8 rounded-full ${Number(selectedPermission) &
                                                            Number(permission.map ?? 0)
                                                            ? 'bg-brand-green'
                                                            : 'bg-brand-profile'
                                                            }`}
                                                    >
                                                        <div
                                                            className={`absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform ${Number(selectedPermission) & (Number(permission.map ?? 0)) ? 'translate-x-6' : 'translate-x-0'}`}
                                                        />
                                                    </div>
                                                </div>

                                            </div>
                                        ))}
                                    </div>
                                </div>

                            </div>
                        </div>
                    </>

                )}

                {error && (
                    <div className="flex flex-col items-center py-64 px-8 text-center">
                        <div className="border-8 border-gray-600 rounded-full p-6">
                            <Icon name="x" size="70" />
                        </div>
                        <h1 className="text-2xl font-bold mt-4">{error}</h1>

                        <button
                            onClick={() => navigate("/")}
                            className="bg-brand-green hover:bg-green-500 transition-all text-white font-semibold py-2 px-4 rounded-lg hover:bg-brand-hover focus:outline-none mt-4"
                        >
                            Go back
                        </button>
                    </div>
                )}

                {!error && botQuery.isFetched && (
                    <>
                        <div className="mt-2 text-center flex justify-between w-full px-8 mb-4">

                            <a
                                href="/"
                                className="text-brand-green font-semibold hover:underline focus:outline-none"
                            >
                                Cancel
                            </a>
                            {!query.data?.user && (
                                <Login>
                                <button
                                    className="bg-brand-green hover:bg-green-500 transition-all text-white font-semibold py-2 px-4 rounded-lg hover:bg-brand-hover focus:outline-none"
                                    >
                                    Login
                                </button>
                                </Login>
                            ) || (
                                    <button
                                        disabled={selectedServerId === ""}
                                        onClick={addBot}
                                        className="bg-brand-green text-white transition-all font-semibold py-2 px-4 rounded-lg hover:bg-brand-hover focus:outline-none disabled:opacity-50"
                                    >
                                        Authorize
                                    </button>

                                )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
